import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Eye, Save, QrCode, Image, Loader2, Trash2, GripVertical, CheckCircle, X, Heart, Music, Type, Palette } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'romantic', icon: Heart, color: '#DDA7A5' },
  { id: 'warm', icon: Heart, color: '#F4D5C9' },
  { id: 'nostalgic', icon: Heart, color: '#C9B896' },
  { id: 'minimal', icon: Type, color: '#C5DB7B' },
  { id: 'elegant', icon: Palette, color: '#C3B1E1' },
  { id: 'tribute', icon: Heart, color: '#6B655F' },
];

const ACCENT_COLORS = [
  { color: '#DDA7A5', name: 'Rose' },
  { color: '#F4D5C9', name: 'Peach' },
  { color: '#C3B1E1', name: 'Lavender' },
  { color: '#C5DB7B', name: 'Sage' },
  { color: '#F9E4A6', name: 'Gold' },
  { color: '#6B655F', name: 'Slate' },
];

const LAYOUT_STYLES = ['story', 'gallery', 'timeline', 'minimal'];

export default function MemoryPageBuilder() {
  const { pageId } = useParams();
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const autosaveTimer = useRef(null);

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [showPreview, setShowPreview] = useState(false);
  const [form, setForm] = useState({
    title: '', message: '', template: 'romantic',
    accent_color: '#DDA7A5', font_style: 'elegant',
    layout_style: 'story', music_mood: null
  });

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (pageId && pageId !== 'new') {
      axios.get(`${API}/memory-pages/${pageId}`, { withCredentials: true })
        .then(r => {
          setPage(r.data);
          setForm({
            title: r.data.title || '',
            message: r.data.message || '',
            template: r.data.template || 'romantic',
            accent_color: r.data.accent_color || '#DDA7A5',
            font_style: r.data.font_style || 'elegant',
            layout_style: r.data.layout_style || 'story',
            music_mood: r.data.music_mood || null
          });
        })
        .catch(() => navigate('/memories'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [pageId, user, navigate]);

  // Autosave with debounce
  const triggerAutosave = useCallback((data) => {
    if (!page) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        setAutosaveStatus('saving');
        await axios.put(`${API}/memory-pages/${page.page_id}/autosave`, data, { withCredentials: true });
        setAutosaveStatus('saved');
        setTimeout(() => setAutosaveStatus(null), 2000);
      } catch {
        setAutosaveStatus('error');
      }
    }, 3000);
  }, [page]);

  const updateForm = (updates) => {
    const newForm = { ...form, ...updates };
    setForm(newForm);
    triggerAutosave(newForm);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (page) {
        const res = await axios.put(`${API}/memory-pages/${page.page_id}`, form, { withCredentials: true });
        setPage(res.data);
        toast.success(lang === 'id' ? 'Tersimpan!' : 'Saved!');
      } else {
        const res = await axios.post(`${API}/memory-pages`, form, { withCredentials: true });
        setPage(res.data);
        navigate(`/memory-builder/${res.data.page_id}`, { replace: true });
        toast.success(lang === 'id' ? 'Memory page dibuat!' : 'Memory page created!');
      }
    } catch { toast.error(t('common.error')); }
    setSaving(false);
  };

  const handlePublish = async () => {
    if (!page) return;
    setPublishing(true);
    try {
      const res = await axios.post(`${API}/memory-pages/${page.page_id}/publish`, {}, { withCredentials: true });
      setPage(res.data);
      toast.success(t('memory_page.qr_ready'));
    } catch { toast.error(t('common.error')); }
    setPublishing(false);
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || !page) return;
    setUploading(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/memory-pages/${page.page_id}/assets`, formData, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        });
        setPage(prev => ({ ...prev, assets: [...(prev.assets || []), res.data] }));
      } catch { toast.error(lang === 'id' ? 'Gagal mengunggah file' : 'Failed to upload file'); }
    }
    toast.success(lang === 'id' ? 'File diunggah!' : 'Files uploaded!');
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDeleteAsset = async (assetId) => {
    if (!page) return;
    try {
      await axios.delete(`${API}/memory-pages/${page.page_id}/assets/${assetId}`, { withCredentials: true });
      setPage(prev => ({ ...prev, assets: prev.assets.filter(a => a.asset_id !== assetId) }));
      toast.success(lang === 'id' ? 'File dihapus' : 'File deleted');
    } catch { toast.error(t('common.error')); }
  };

  const handleUpdateCaption = async (assetId, caption) => {
    if (!page) return;
    try {
      await axios.put(`${API}/memory-pages/${page.page_id}/assets/${assetId}`, { caption }, { withCredentials: true });
      setPage(prev => ({
        ...prev,
        assets: prev.assets.map(a => a.asset_id === assetId ? { ...a, caption } : a)
      }));
    } catch { /* silent */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="mx-auto text-[#C5DB7B] animate-spin mb-4" />
          <p className="text-[#6B655F] font-light">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'content', label: lang === 'id' ? 'Konten' : 'Content' },
    { id: 'design', label: lang === 'id' ? 'Desain' : 'Design' },
    { id: 'media', label: 'Media' },
  ];

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="memory-builder">
      {/* Floating orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="orb orb-rose w-[400px] h-[400px] top-20 -right-40 opacity-20" />
        <div className="orb orb-lavender w-[300px] h-[300px] bottom-40 -left-20 opacity-15" />
      </div>

      <div className="max-w-3xl mx-auto px-5 md:px-12 py-10 md:py-10 relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <span className="overline">{t('memory_page.title')}</span>
            <h1 className="mt-2 font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[#2C2A29]">
              {page ? (lang === 'id' ? 'Edit Memori' : 'Edit Memory') : t('memory_page.create')}
            </h1>
          </div>
          {/* Autosave indicator */}
          <AnimatePresence>
            {autosaveStatus && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 text-xs"
                data-testid="autosave-indicator"
              >
                {autosaveStatus === 'saving' && <><Loader2 size={12} className="animate-spin text-[#C5DB7B]" /><span className="text-[#6B655F]">{lang === 'id' ? 'Menyimpan...' : 'Saving...'}</span></>}
                {autosaveStatus === 'saved' && <><CheckCircle size={12} className="text-[#C5DB7B]" /><span className="text-[#C5DB7B]">{lang === 'id' ? 'Tersimpan' : 'Saved'}</span></>}
                {autosaveStatus === 'error' && <span className="text-red-400">{lang === 'id' ? 'Gagal menyimpan' : 'Save failed'}</span>}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-8 p-1 glass-card-subtle rounded-2xl" data-testid="builder-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white/80 text-[#2C2A29] shadow-sm'
                  : 'text-[#6B655F] hover:text-[#2C2A29]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Tab */}
        {activeTab === 'content' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="glass-card rounded-2xl p-6 md:p-8">
              <label className="text-sm font-medium text-[#2C2A29] mb-2 block">
                {lang === 'id' ? 'Judul Memori' : 'Memory Title'}
              </label>
              <Input
                value={form.title}
                onChange={e => updateForm({ title: e.target.value })}
                placeholder={lang === 'id' ? 'Judul memori Anda...' : 'Your memory title...'}
                className="rounded-xl border-[#EAE5DF] font-light text-lg"
                data-testid="memory-title-input"
              />
            </div>

            <div className="glass-card rounded-2xl p-6 md:p-8">
              <label className="text-sm font-medium text-[#2C2A29] mb-2 block">
                {t('memory_page.message_label')}
              </label>
              <Textarea
                value={form.message}
                onChange={e => updateForm({ message: e.target.value })}
                placeholder={t('memory_page.message_placeholder')}
                className="min-h-[180px] rounded-xl border-[#EAE5DF] font-light resize-none leading-relaxed"
                data-testid="memory-message-input"
              />
              <p className="mt-2 text-xs text-[#6B655F]/60 font-light">
                {lang === 'id' ? 'Tulis pesan personal yang akan dibaca penerima' : 'Write a personal message for the recipient to read'}
              </p>
            </div>
          </motion.div>
        )}

        {/* Design Tab */}
        {activeTab === 'design' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Template Selection */}
            <div className="glass-card rounded-2xl p-6 md:p-8">
              <label className="text-sm font-medium text-[#2C2A29] mb-4 block">{t('memory_page.template')}</label>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {TEMPLATES.map(tmpl => (
                  <button
                    key={tmpl.id}
                    onClick={() => updateForm({ template: tmpl.id })}
                    className={`group p-4 rounded-2xl border-2 text-center transition-all duration-300 ${
                      form.template === tmpl.id
                        ? 'border-[#C5DB7B] bg-white/80 shadow-lg shadow-[#C5DB7B]/10 scale-[1.02]'
                        : 'border-transparent glass-card-subtle hover:border-[#C5DB7B]/30 hover:scale-[1.01]'
                    }`}
                    data-testid={`template-${tmpl.id}`}
                  >
                    <div className="w-8 h-8 mx-auto mb-2 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${tmpl.color}20` }}>
                      <tmpl.icon size={14} style={{ color: tmpl.color }} />
                    </div>
                    <span className="text-[10px] font-medium text-[#2C2A29] block">{t(`memory_page.template_${tmpl.id}`)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Accent Color */}
            <div className="glass-card rounded-2xl p-6 md:p-8">
              <label className="text-sm font-medium text-[#2C2A29] mb-4 block">
                {lang === 'id' ? 'Warna Aksen' : 'Accent Color'}
              </label>
              <div className="flex gap-4">
                {ACCENT_COLORS.map(({ color, name }) => (
                  <button
                    key={color}
                    onClick={() => updateForm({ accent_color: color })}
                    className={`group flex flex-col items-center gap-2 transition-all`}
                    data-testid={`color-${color}`}
                  >
                    <div
                      className={`w-12 h-12 rounded-full transition-all duration-300 ${
                        form.accent_color === color
                          ? 'ring-2 ring-offset-4 ring-[#2C2A29] scale-110'
                          : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-[10px] text-[#6B655F] font-light">{name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Layout Style */}
            <div className="glass-card rounded-2xl p-6 md:p-8">
              <label className="text-sm font-medium text-[#2C2A29] mb-4 block">
                {lang === 'id' ? 'Gaya Tata Letak' : 'Layout Style'}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {LAYOUT_STYLES.map(style => (
                  <button
                    key={style}
                    onClick={() => updateForm({ layout_style: style })}
                    className={`p-4 rounded-2xl border-2 text-center transition-all ${
                      form.layout_style === style
                        ? 'border-[#C5DB7B] bg-white/80 shadow-md'
                        : 'border-transparent glass-card-subtle hover:border-[#C5DB7B]/30'
                    }`}
                    data-testid={`layout-${style}`}
                  >
                    <span className="text-xs font-medium text-[#2C2A29] capitalize">{
                      lang === 'id'
                        ? { story: 'Cerita', gallery: 'Galeri', timeline: 'Kronologi', minimal: 'Minimalis' }[style]
                        : style.charAt(0).toUpperCase() + style.slice(1)
                    }</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {!page ? (
              <div className="glass-card rounded-2xl p-8 text-center">
                <Save size={32} className="mx-auto text-[#C5DB7B] mb-4" strokeWidth={1.5} />
                <p className="text-sm text-[#6B655F] font-light">
                  {lang === 'id' ? 'Simpan draft terlebih dahulu untuk mengunggah media' : 'Save draft first to upload media'}
                </p>
                <Button onClick={handleSave} className="mt-4 bg-[#D4859B] hover:bg-[#C07088] text-white rounded-full px-6" data-testid="save-before-upload">
                  <Save size={14} className="mr-2" /> {t('memory_page.save_draft')}
                </Button>
              </div>
            ) : (
              <>
                {/* Upload Zone */}
                <div className="glass-card rounded-2xl p-6 md:p-8">
                  <label className="text-sm font-medium text-[#2C2A29] mb-4 block">{t('memory_page.upload_photos')}</label>
                  <input
                    type="file"
                    ref={fileRef}
                    onChange={handleUpload}
                    className="hidden"
                    accept="image/*,video/*"
                    multiple
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-full p-10 border-2 border-dashed border-[#EAE5DF] rounded-2xl text-center hover:border-[#C5DB7B] hover:bg-[#C5DB7B]/5 transition-all duration-300 group"
                    data-testid="upload-media-btn"
                  >
                    {uploading ? (
                      <Loader2 size={28} className="mx-auto text-[#C5DB7B] animate-spin" />
                    ) : (
                      <>
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl glass-card-subtle flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Upload size={24} className="text-[#6B655F] group-hover:text-[#C5DB7B] transition-colors" strokeWidth={1.5} />
                        </div>
                        <p className="text-sm text-[#6B655F] font-light">
                          {lang === 'id' ? 'Klik atau seret foto & video ke sini' : 'Click or drag photos & videos here'}
                        </p>
                        <p className="text-[10px] text-[#6B655F]/50 mt-1 font-light">
                          {lang === 'id' ? 'Mendukung banyak file sekaligus' : 'Supports multiple files at once'}
                        </p>
                      </>
                    )}
                  </button>
                </div>

                {/* Uploaded Assets */}
                {page.assets?.length > 0 && (
                  <div className="glass-card rounded-2xl p-6 md:p-8">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-[#2C2A29]">
                        {page.assets.length} {lang === 'id' ? 'media diunggah' : 'media uploaded'}
                      </label>
                    </div>
                    <div className="space-y-4">
                      {page.assets.map((asset, idx) => (
                        <motion.div
                          key={asset.asset_id}
                          layout
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="flex gap-4 items-start glass-card-subtle rounded-xl p-3 group"
                          data-testid={`asset-${asset.asset_id}`}
                        >
                          <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#F3EEEA] flex-shrink-0">
                            {asset.content_type?.startsWith('video') ? (
                              <video src={`${process.env.REACT_APP_BACKEND_URL}${asset.url}`} className="w-full h-full object-cover" />
                            ) : (
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${asset.url}`} alt="" className="w-full h-full object-cover" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <Input
                              value={asset.caption || ''}
                              onChange={e => {
                                const newCaption = e.target.value;
                                setPage(prev => ({
                                  ...prev,
                                  assets: prev.assets.map(a => a.asset_id === asset.asset_id ? { ...a, caption: newCaption } : a)
                                }));
                              }}
                              onBlur={e => handleUpdateCaption(asset.asset_id, e.target.value)}
                              placeholder={lang === 'id' ? 'Tambahkan keterangan...' : 'Add caption...'}
                              className="rounded-lg border-[#EAE5DF] text-xs font-light h-8"
                              data-testid={`caption-${asset.asset_id}`}
                            />
                            <p className="text-[10px] text-[#6B655F]/50 mt-1">
                              {asset.content_type?.startsWith('video') ? 'Video' : 'Photo'}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteAsset(asset.asset_id)}
                            className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-50 text-red-400 transition-all"
                            data-testid={`delete-asset-${asset.asset_id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* QR Code Section */}
        {page?.status === 'published' && page?.qr_code && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-8 glass-card-strong rounded-2xl p-8 text-center"
            data-testid="qr-section"
          >
            <QrCode size={28} className="mx-auto text-[#D4859B] mb-3" />
            <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#2C2A29] mb-2">{t('memory_page.qr_ready')}</h3>
            <p className="text-sm text-[#6B655F] font-light mb-6">{t('memory_page.qr_desc')}</p>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${page.qr_code}`} alt="QR Code" className="w-48 h-48 mx-auto rounded-xl shadow-lg" />
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex gap-3 flex-wrap">
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="outline"
            className="flex-1 min-w-[140px] border-[#EAE5DF] rounded-full py-6"
            data-testid="save-draft-btn"
          >
            <Save size={16} className="mr-2" />
            {saving ? '...' : t('memory_page.save_draft')}
          </Button>
          {page && page.status !== 'published' && (
            <Button
              onClick={handlePublish}
              disabled={publishing || !form.title}
              className="flex-1 min-w-[140px] bg-[#D4859B] hover:bg-[#C07088] text-white rounded-full py-6"
              data-testid="publish-btn"
            >
              <QrCode size={16} className="mr-2" />
              {publishing ? '...' : t('memory_page.publish')}
            </Button>
          )}
          {page && (
            <Button
              onClick={() => navigate(`/memory/${page.page_id}`)}
              variant="outline"
              className="border-[#EAE5DF] rounded-full py-6 px-6"
              data-testid="preview-btn"
            >
              <Eye size={16} className="mr-1" />
              {t('memory_page.preview')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
